import { Schema, Effect } from "effect";
import { describeIntRange } from "../openapi-query.js";
import { HttpApiEndpoint, HttpApiError, HttpApiGroup } from "effect/http-api";
import {
  HeatFilterResultsResponseSchema,
  HeatFilterV3ResponseSchema,
  RealtimeScoringDefinitionResponseSchema,
  RealtimeScoringDefinitionUpdateSchema,
  RealtimeScoringCanonicalCanaryApprovalResponseSchema,
  RealtimeScoringCanonicalCanaryApprovalUpdateSchema,
  RealtimeScoringExecutionModeResponseSchema,
  RealtimeScoringExecutionModeUpdateSchema,
  RealtimeScoringMetricCatalogResponseSchema,
  RealtimeScoringMigrationAssessmentResponseSchema,
  RealtimeScoringMigrationCandidateInventoryResponseSchema,
  RealtimeScoringSourceIndexReadinessResponseSchema,
  RealtimeScoringResultsResponseSchema,
  RealtimeScoringShadowResultsResponseSchema,
  ZapierFilterResultResponseSchema,
} from "./models.js";
import { UUID } from "../uuid.js";

// Define path parameters
const heatFilterIdParam = UUID;
export const RealtimeScoringPagination = Schema.Struct({
  offset: Schema.NumberFromString.pipe(
    Schema.check(Schema.isInt()),
    Schema.check(Schema.isGreaterThanOrEqualTo(0)),
    describeIntRange({ minimum: 0 })
  ).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => 0)),
    Schema.withConstructorDefault(Effect.sync(() => 0))
  ),
  limit: Schema.NumberFromString.pipe(
    Schema.check(Schema.isInt()),
    Schema.check(Schema.isGreaterThan(0)),
    Schema.check(Schema.isLessThanOrEqualTo(100)),
    describeIntRange({ minimum: 1, maximum: 100 })
  ).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => 100)),
    Schema.withConstructorDefault(Effect.sync(() => 100))
  ),
});

/** Optional, review-only currency selected for a legacy revenue migration draft. */
export const RealtimeScoringMigrationAssessmentParams = Schema.Struct({
  revenueCurrency: Schema.optional(
    Schema.String.pipe(Schema.check(Schema.isPattern(/^[A-Z]{3}$/u)))
  ),
});

const getFilterByIdEndpoint = HttpApiEndpoint.get("getFilterById", "/filters/:heatFilterId", {
  params: { heatFilterId: heatFilterIdParam },
  success: HeatFilterV3ResponseSchema,
  error: [
    HttpApiError.InternalServerErrorNoContent,
    HttpApiError.BadRequestNoContent,
    HttpApiError.NotFoundNoContent,
    HttpApiError.ForbiddenNoContent,
    HttpApiError.NotImplementedNoContent,
  ],
});

const getRealtimeScoringMetricCatalogEndpoint = HttpApiEndpoint.get(
  "getRealtimeScoringMetricCatalog",
  "/filters/realtime-metric-catalog",
  {
    success: RealtimeScoringMetricCatalogResponseSchema,
    error: HttpApiError.InternalServerErrorNoContent,
  }
);

const getRealtimeScoringSourceIndexReadinessEndpoint = HttpApiEndpoint.get(
  "getRealtimeScoringSourceIndexReadiness",
  "/filters/:heatFilterId/realtime-scoring-index-readiness",
  {
    params: { heatFilterId: heatFilterIdParam },
    success: RealtimeScoringSourceIndexReadinessResponseSchema,
    error: [
      HttpApiError.BadRequestNoContent,
      HttpApiError.ForbiddenNoContent,
      HttpApiError.NotFoundNoContent,
      HttpApiError.InternalServerErrorNoContent,
    ],
  }
);

const getRealtimeScoringMigrationAssessmentEndpoint = HttpApiEndpoint.get(
  "getRealtimeScoringMigrationAssessment",
  "/filters/:heatFilterId/realtime-migration-assessment",
  {
    params: { heatFilterId: heatFilterIdParam },
    query: RealtimeScoringMigrationAssessmentParams,
    success: RealtimeScoringMigrationAssessmentResponseSchema,
    error: [
      HttpApiError.BadRequestNoContent,
      HttpApiError.ForbiddenNoContent,
      HttpApiError.NotFoundNoContent,
      HttpApiError.InternalServerErrorNoContent,
    ],
  }
);

const getRealtimeScoringMigrationCandidateInventoryEndpoint = HttpApiEndpoint.get(
  "getRealtimeScoringMigrationCandidateInventory",
  "/filters/realtime-migration-candidates",
  {
    success: RealtimeScoringMigrationCandidateInventoryResponseSchema,
    error: HttpApiError.InternalServerErrorNoContent,
  }
);

const getFilterResultsByIdEndpoint = HttpApiEndpoint.get(
  "getFilterResultsById",
  "/filters/:heatFilterId/results",
  {
    params: { heatFilterId: heatFilterIdParam },
    query: RealtimeScoringPagination,
    success: HeatFilterResultsResponseSchema,
    error: [
      HttpApiError.InternalServerErrorNoContent,
      HttpApiError.BadRequestNoContent,
      HttpApiError.NotFoundNoContent,
      HttpApiError.ForbiddenNoContent,
      HttpApiError.NotImplementedNoContent,
      HttpApiError.ServiceUnavailableNoContent,
    ],
  }
);

const putRealtimeScoringDefinitionEndpoint = HttpApiEndpoint.put(
  "putRealtimeScoringDefinition",
  "/filters/:heatFilterId/realtime-definition",
  {
    params: { heatFilterId: heatFilterIdParam },
    payload: RealtimeScoringDefinitionUpdateSchema,
    success: RealtimeScoringDefinitionResponseSchema,
    error: [
      HttpApiError.BadRequestNoContent,
      HttpApiError.ConflictNoContent,
      HttpApiError.ForbiddenNoContent,
      HttpApiError.InternalServerErrorNoContent,
      HttpApiError.NotFoundNoContent,
    ],
  }
);

const getRealtimeScoringResultsEndpoint = HttpApiEndpoint.get(
  "getRealtimeScoringResults",
  "/filters/:heatFilterId/realtime-results",
  {
    params: { heatFilterId: heatFilterIdParam },
    query: RealtimeScoringPagination,
    success: RealtimeScoringResultsResponseSchema,
    error: [
      HttpApiError.BadRequestNoContent,
      HttpApiError.ForbiddenNoContent,
      HttpApiError.InternalServerErrorNoContent,
      HttpApiError.NotFoundNoContent,
      HttpApiError.ServiceUnavailableNoContent,
    ],
  }
);

const getRealtimeScoringShadowResultsEndpoint = HttpApiEndpoint.get(
  "getRealtimeScoringShadowResults",
  "/filters/:heatFilterId/realtime-shadow-results",
  {
    params: { heatFilterId: heatFilterIdParam },
    query: RealtimeScoringPagination,
    success: RealtimeScoringShadowResultsResponseSchema,
    error: [
      HttpApiError.BadRequestNoContent,
      HttpApiError.ForbiddenNoContent,
      HttpApiError.InternalServerErrorNoContent,
      HttpApiError.NotFoundNoContent,
      HttpApiError.ServiceUnavailableNoContent,
    ],
  }
);

const putRealtimeScoringExecutionModeEndpoint = HttpApiEndpoint.put(
  "putRealtimeScoringExecutionMode",
  "/filters/:heatFilterId/realtime-execution-mode",
  {
    params: { heatFilterId: heatFilterIdParam },
    payload: RealtimeScoringExecutionModeUpdateSchema,
    success: RealtimeScoringExecutionModeResponseSchema,
    error: [
      HttpApiError.BadRequestNoContent,
      HttpApiError.ConflictNoContent,
      HttpApiError.ForbiddenNoContent,
      HttpApiError.InternalServerErrorNoContent,
      HttpApiError.NotFoundNoContent,
      HttpApiError.ServiceUnavailableNoContent,
    ],
  }
);

const putRealtimeScoringCanonicalCanaryApprovalEndpoint = HttpApiEndpoint.put(
  "putRealtimeScoringCanonicalCanaryApproval",
  "/filters/:heatFilterId/realtime-canonical-canary-approval",
  {
    params: { heatFilterId: heatFilterIdParam },
    payload: RealtimeScoringCanonicalCanaryApprovalUpdateSchema,
    success: RealtimeScoringCanonicalCanaryApprovalResponseSchema,
    error: [
      HttpApiError.BadRequestNoContent,
      HttpApiError.ConflictNoContent,
      HttpApiError.ForbiddenNoContent,
      HttpApiError.InternalServerErrorNoContent,
      HttpApiError.NotFoundNoContent,
      HttpApiError.ServiceUnavailableNoContent,
    ],
  }
);

const getZapierFilterResultResponseEndpoint = HttpApiEndpoint.get(
  "getZapierFilterResults",
  "/filters/zapier/:heatFilterId/results",
  {
    params: { heatFilterId: heatFilterIdParam },
    query: Schema.Struct({
      offset: Schema.NumberFromString.pipe(
        Schema.withDecodingDefaultType(Effect.sync(() => 0)),
        Schema.withConstructorDefault(Effect.sync(() => 0))
      ),
      limit: Schema.NumberFromString.pipe(
        Schema.withDecodingDefaultType(Effect.sync(() => 100)),
        Schema.withConstructorDefault(Effect.sync(() => 100))
      ),
    }),
    success: ZapierFilterResultResponseSchema,
    error: [
      HttpApiError.InternalServerErrorNoContent,
      HttpApiError.BadRequestNoContent,
      HttpApiError.NotFoundNoContent,
      HttpApiError.ForbiddenNoContent,
      HttpApiError.NotImplementedNoContent,
    ],
  }
);

// Create the filters group
export const FiltersV3Group = HttpApiGroup.make("filtersV3")
  .add(getRealtimeScoringMetricCatalogEndpoint)
  .add(getRealtimeScoringSourceIndexReadinessEndpoint)
  .add(getRealtimeScoringMigrationCandidateInventoryEndpoint)
  .add(getRealtimeScoringMigrationAssessmentEndpoint)
  .add(getFilterByIdEndpoint)
  .add(getZapierFilterResultResponseEndpoint)
  .add(getFilterResultsByIdEndpoint)
  .add(putRealtimeScoringDefinitionEndpoint)
  .add(putRealtimeScoringCanonicalCanaryApprovalEndpoint)
  .add(putRealtimeScoringExecutionModeEndpoint)
  .add(getRealtimeScoringResultsEndpoint)
  .add(getRealtimeScoringShadowResultsEndpoint);

export * from "./models.js";
